import { Suspense } from 'react';
import { useLingui } from '@lingui/react/macro';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Zap } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { useTranslation } from '@/lib/i18n';
import { Image } from '@/routes/-components/image';
import { useNgoAreaUrl } from '@/lib/ngo-area';
import { Eyebrow, PillLink } from '../primitives';
import { NGO_APPLICATION_FORM_URL } from '../external-links';
import { Expand, ExpandGroup, EXPAND_STEP_ROOMY } from '../expand';

/**
 * The partner count on the stat card. Live off the same global-stats query the rest of the site
 * counts on, so the headline number and the directory below it can never disagree.
 *
 * Its own component so the Suspense boundary wraps the figure alone — the photo and the headline
 * beside it need no data and must not wait for one.
 */
function PartnerCount() {
	const { data: stats } = useSuspenseQuery(trpc.portal.getWorldcooGlobalStats.queryOptions());
	const { language } = useTranslation();
	return <>{stats.socialOrganizations.toLocaleString(language, { useGrouping: true })}</>;
}

/**
 * "Nacimos con un propósito muy claro" — the ONG-colaboradoras hero.
 *
 * The hand-off is a 1440x786 frame holding four absolutely-placed boxes: a 536-wide copy column at
 * x=54, then three cards on a 24px gutter — the photograph (369x601) and, stacked beside it, the
 * stat card (369x249) over the globe card (369x328). 601 = 249 + 24 + 328, so the two columns end
 * flush; that is the whole reason those heights are what they are.
 *
 * Rebuilt as a grid rather than absolute positioning so it can collapse on mobile, where the hand-off
 * stacks copy → photo → stat → globe in one column.
 */
export function Hero() {
	const { t } = useLingui();
	const ngoArea = useNgoAreaUrl();
	return (
		<ExpandGroup step={EXPAND_STEP_ROOMY} asChild>
			<section className="mx-auto grid max-w-[1440px] items-center gap-6 px-6 py-12 md:py-16 lg:grid-cols-[536fr_369fr_369fr] xl:px-[54px]">
				<div className="flex flex-col items-start gap-6">
					{/* The hand-off boxes this eyebrow in white with an 8px radius — it sits on the page's own
				    white here, so the plate is invisible and only the padding it implies is kept. */}
					<Eyebrow>{t`Partner NGOs`}</Eyebrow>
					<div className="flex flex-col gap-2">
						<h1 className="text-[2.5rem] leading-[1.143] font-medium tracking-[-0.02em] md:text-[3.5rem]">
							{t`We were born with a very clear purpose`}
						</h1>
						<p className="max-w-[481px] text-base leading-[1.5]">
							{t`Helping social entities find the funding they need to carry out their projects all over the world.`}
						</p>
					</div>
					{/* The mobile frame drops both pills from the hero — the phone layout goes straight from the
				    paragraph to the photograph, and the header's menu carries the CTA instead. Following the
				    hand-off rather than second-guessing it; if conversion wants them back on mobile, this is
				    the one line to change. */}
					<div className="hidden flex-wrap gap-2 lg:flex">
						<PillLink variant="ink" href={NGO_APPLICATION_FORM_URL}>{t`Join Worldcoo`}</PillLink>
						<PillLink variant="outline" href={ngoArea('/login')}>{t`I already collaborate`}</PillLink>
					</div>
				</div>

				{/* 369x601 in the hand-off — the taller column, and the reason the two cards beside it add up
			    to the same 601. The photograph is already cut to that ratio; `h-60` on mobile crops a band out
			    of its middle. `height` asks for 2x, which the 472x768 source falls well short of — imgproxy
			    clamps to the source rather than enlarging, so this serves the best the file has. A full-size
			    export of this crop would need no change here beyond the file itself. */}
				{/* The `-v2` is cache-busting, not housekeeping. The imgproxy URL carries this path base64-encoded,
			    and the CDN's cache key normalises `Accept-Encoding`, so an asset replaced in place keeps serving
			    the old bytes to every brotli-capable browser — all of them — for the full year of its `max-age`,
			    with no invalidation anywhere in the repo to clear it. Replacing a photograph means renaming it. */}
				<Expand index={0} asChild>
					<div className="relative h-60 overflow-hidden rounded-3xl lg:h-[601px]">
						<Image
							src="/redesign/partner-ngos/hero-children-circle-v2.jpg"
							alt={t`Children in a circle, arms around each other's shoulders, smiling down at the camera`}
							height={1202}
							aspectRatio={369 / 601}
							objectFit="cover"
							priority
							className="absolute inset-0 size-full object-cover!"
						/>
						{/* The hand-off lays black at 10% over the photograph — it carries no type, so this is
					    tone-matching against the two cards beside it rather than legibility. */}
						<div aria-hidden className="absolute inset-0 bg-black/10" />
					</div>
				</Expand>

				<div className="flex flex-col gap-6">
					<Expand index={1} asChild>
						<div className="bg-sage flex h-[195px] flex-col justify-between gap-4 rounded-3xl p-6 lg:h-[249px] lg:gap-8 lg:p-8">
							<Zap className="text-ink size-9 shrink-0 fill-current" strokeWidth={0} />
							<div className="flex flex-col">
								<p className="text-ink text-5xl leading-[1.3] font-semibold tabular-nums md:text-[3.5rem]">
									<Suspense fallback={<span className="opacity-40">—</span>}>
										<PartnerCount />
									</Suspense>
								</p>
								<p className="text-ink text-xl leading-[1.5] font-medium md:text-2xl">{t`Partner NGOs`}</p>
							</div>
						</div>
					</Expand>

					{/* The globe is the export's own artwork at its designed 369x328 box. Centred and clipped
				    rather than stretched — the illustration is not on the card's aspect ratio. */}
					<Expand index={1.5} asChild>
						<div className="bg-apricot flex h-[195px] items-center justify-center overflow-hidden rounded-3xl lg:h-[328px]">
							<img
								src="/redesign/partner-ngos/globe.svg"
								alt={t`A globe marked with the countries where the NGOs work`}
								className="h-full w-auto max-w-full lg:h-auto lg:w-full"
								width={369}
								height={328}
							/>
						</div>
					</Expand>
				</div>
			</section>
		</ExpandGroup>
	);
}
